import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import type { TeamEntry } from '../../services/team-entries';

interface RoundSquadAthletesProps {
  /** The squad's athletes in this round (squadInRound). */
  entries: TeamEntry[];
  loading: boolean;
  loadFailed: boolean;
  /** Names the squad on each row: only worth it for somebody managing two or more. */
  showTeam: boolean;
}

/**
 * A round's card unfolded for a squad's team manager, before the round starts:
 * who of theirs climbs it, and whether the desk has confirmed them yet — only a
 * confirmed entry has a card to mark once the clock starts. The round's own
 * page waits until then, since before it there is nothing on it to do.
 */
export default function RoundSquadAthletes({ entries, loading, loadFailed, showTeam }: RoundSquadAthletesProps) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="flex justify-center py-2">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (loadFailed) return <p className="text-sm text-destructive">{t('teamEntries.loadError')}</p>;
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="round-squad-empty">
        {t('competitionRounds.squad.empty')}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <ul className="divide-y" data-testid="round-squad-list">
        {entries.map((entry) => (
          <li key={entry.id} className="flex items-center gap-2 py-2" data-testid="round-squad-athlete">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{entry.full_name}</p>
              <p className="truncate text-xs text-muted-foreground">
                {[entry.category_name, showTeam && entry.team_name].filter(Boolean).join(' · ')}
                {/* On every turn of the class until one is booked, as on the boards. */}
                {entry.turn_to_choose && (
                  <span className="text-amber-700 dark:text-amber-400">
                    {' · '}
                    {t('stageEntry.turnChoice.pending')}
                  </span>
                )}
              </p>
            </div>
            {entry.confirmed ? (
              <Badge variant="secondary" className="shrink-0" data-testid="round-squad-confirmed">
                {t('teamEntries.confirmed')}
              </Badge>
            ) : (
              <span className="shrink-0 text-xs text-muted-foreground" data-testid="round-squad-unconfirmed">
                {t('competitionRounds.squad.notConfirmed')}
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted-foreground">{t('competitionRounds.squad.hint')}</p>
    </div>
  );
}
